import React, { useEffect, useState } from "react";
import { HashRouter, Navigate, Route, Routes, useLocation } from "react-router-dom";
import {
  motion,
  MotionConfig,
  useScroll,
  useSpring,
  useTransform,
} from "framer-motion";
import { Github, Linkedin, Mail } from "lucide-react";

import { personalInfo } from "./data/personalInfo";

import { Cursor } from "./components/Cursor";
import { AmbientField } from "./components/fun/AmbientField";
import { NothingGlyph } from "./components/fun/NothingGlyph";
import { Passport } from "./components/fun/Passport";
import { SentToast } from "./components/fun/Overlays";
import { HomePage } from "./pages/HomePage";
import { GyanUdaanPage } from "./pages/GyanUdaanPage";

const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" in window ? "instant" : "auto" });
  }, [pathname]);
  return null;
};

const AppShell = () => {
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [isHovered, setIsHovered] = useState(false);
  const [ageFlow, setAgeFlow] = useState("00.000000000");
  const [sent, setSent] = useState(false);
  const [passportOpen, setPassportOpen] = useState(false);

  const { scrollYProgress, scrollY } = useScroll();
  const scaleX = useSpring(scrollYProgress, { stiffness: 100, damping: 30, restDelta: 0.001 });

  const yHero = useTransform(scrollY, [0, 500], [0, 150]);
  const opacityHero = useTransform(scrollY, [0, 300], [1, 0.5]);

  useEffect(() => {
    const updateMouse = (e) => setMousePosition({ x: e.clientX, y: e.clientY });
    window.addEventListener("mousemove", updateMouse);
    return () => window.removeEventListener("mousemove", updateMouse);
  }, []);

  useEffect(() => {
    console.log(
      "%c SYSTEM BOOT_SEQUENCE_INITIATED \n%c Welcome to the architecture of Prasanna Dangol ",
      "background: #000; color: #ff0000; font-size: 14px; padding: 10px;",
      "background: #fff; color: #000; font-size: 12px; padding: 5px;"
    );
  }, []);

  useEffect(() => {
    const birthDate = new Date(personalInfo.birthDate);
    const updateAge = () => {
      const now = new Date();
      let years = now.getFullYear() - birthDate.getFullYear();
      const hasHadBirthday =
        now.getMonth() > birthDate.getMonth() ||
        (now.getMonth() === birthDate.getMonth() && now.getDate() >= birthDate.getDate());
      if (!hasHadBirthday) years--;
      const lastBirthday = new Date(
        now.getFullYear() - (hasHadBirthday ? 0 : 1),
        birthDate.getMonth(),
        birthDate.getDate()
      );
      const nextBirthday = new Date(
        lastBirthday.getFullYear() + 1,
        birthDate.getMonth(),
        birthDate.getDate()
      );
      const fractional = (now - lastBirthday) / (nextBirthday - lastBirthday);
      setAgeFlow((years + fractional).toFixed(9).padStart(12, "0"));
    };
    const timer = setInterval(updateAge, 50);
    updateAge();
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    if (!sent) return;
    const t = setTimeout(() => setSent(false), 3600);
    return () => clearTimeout(t);
  }, [sent]);

  return (
    <div
      className="min-h-screen bg-grid-pattern relative selection:bg-nothing-red selection:text-white pb-32 overflow-x-hidden"
      style={{ backgroundPosition: `${mousePosition.x / 50}px ${mousePosition.y / 50}px` }}
    >
      <motion.div
        className="fixed top-0 left-0 right-0 h-1 bg-nothing-red origin-left z-50"
        style={{ scaleX }}
      />
      <Cursor mousePosition={mousePosition} isHovered={isHovered} />
      <NothingGlyph onOpen={() => setPassportOpen(true)} />
      <Passport open={passportOpen} onClose={() => setPassportOpen(false)} />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-12 md:py-24">
        <Routes>
          <Route
            path="/"
            element={
              <HomePage
                setHovered={setIsHovered}
                yHero={yHero}
                opacityHero={opacityHero}
                ageFlow={ageFlow}
                onSend={() => setSent(true)}
              />
            }
          />
          <Route path="/gyanudaan" element={<GyanUdaanPage setHovered={setIsHovered} />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>

      <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50">
        <motion.div
          initial={{ y: 100 }}
          animate={{ y: 0 }}
          transition={{ delay: 1, type: "spring" }}
          className="glass-panel px-6 py-3 rounded-full flex items-center gap-6 shadow-2xl border border-white/20"
        >
          {[Linkedin, Github, Mail].map((Icon, i) => (
            <button
              key={i}
              className="text-gray-600 hover:text-black hover:scale-110 transition-all"
              onClick={() => i === 2 ? window.location.href = `mailto:${personalInfo.email}` : window.open(i === 0 ? personalInfo.linkedin : personalInfo.github)}
            >
              <Icon size={20} />
            </button>
          ))}
        </motion.div>
      </div>
      <AmbientField />
      <SentToast sent={sent} />
    </div>
  );
};

const App = () => (
  <MotionConfig reducedMotion="user">
    <HashRouter>
      <ScrollToTop />
      <AppShell />
    </HashRouter>
  </MotionConfig>
);

export default App;
